/**
 * Super admins: is it safe to deploy right now? (services/deploy_status.py)
 * Green when nothing automated is mid-step; amber while something is (a
 * deploy would cut through it); a red count for anything already stuck.
 * Click for the detail.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Item = { label: string; since: string | null };
type Check = { key: string; label: string; busy?: number; stuck?: number; busyItems?: Item[]; stuckItems?: Item[]; error?: string };
type Status = {
  state: "safe" | "busy" | "unknown"; busy: number; stuck: number; busyMinutes: number;
  checks: Check[]; waiting: { key: string; label: string; count: number }[];
};

const ago = (iso: string | null) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

export function DeployStatusPill() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const q = useQuery({
    queryKey: ["deploy-status"],
    queryFn: () => api.client.get("/admin/deploy-status").then((r) => r.data as Status),
    refetchInterval: open ? 5_000 : 20_000,
  });
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const s = q.data;
  const state = q.isError ? "unknown" : s?.state ?? "unknown";
  const text = q.isLoading ? "Checking…"
    : state === "safe" ? "Safe to deploy"
    : state === "busy" ? `Busy: ${s!.busy} in progress`
    : "Can't tell";
  const tone = state === "safe" ? "border-status-success/30 bg-status-success/10 text-status-success"
    : state === "busy" ? "border-status-warning/40 bg-status-warning-bg text-status-warning"
    : "border-surface-border bg-surface-secondary text-dark-400";

  return (
    <div className="relative" ref={box}>
      <button onClick={() => setOpen(!open)} title="Anything automated mid-step that a deploy would interrupt"
        className={cn("inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-caption font-medium", tone)}>
        <span className={cn("w-2 h-2 rounded-full", state === "safe" ? "bg-status-success" : state === "busy" ? "bg-status-warning animate-pulse" : "bg-dark-300")} />
        {text}
        {!!s?.stuck && <span className="ml-1 px-1.5 rounded-full bg-status-error text-white text-2xs">{s.stuck} stuck</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-h-[70vh] overflow-y-auto rounded-2xl border border-surface-border bg-white shadow-xl z-50 p-4">
          <p className="text-body-sm font-medium text-dark-800">
            {state === "safe" ? "Nothing automated is mid-step." : state === "busy" ? "Wait before deploying." : "Couldn't check everything."}
          </p>
          <p className="text-caption text-dark-400 mt-0.5">
            A deploy restarts the API, and the automated jobs run inside it. In progress means touched in the last {s?.busyMinutes ?? 15} minutes.
          </p>
          {q.isError && <p className="text-caption text-status-error mt-2">The check itself failed. Try again in a moment.</p>}
          {s?.checks.filter((c) => c.busy).map((c) => (
            <Section key={c.key} title={`${c.label} (${c.busy})`} tone="text-status-warning" items={c.busyItems || []} />
          ))}
          {s?.checks.filter((c) => c.stuck).map((c) => (
            <Section key={`${c.key}-stuck`} title={`Stuck: ${c.label.toLowerCase()} (${c.stuck})`} tone="text-status-error"
              items={c.stuckItems || []} note="Interrupted earlier. Doesn't block a deploy, but needs checking against Salesforce or iFAST." />
          ))}
          {s?.checks.filter((c) => c.error).map((c) => (
            <Section key={`${c.key}-err`} title={`Couldn't check: ${c.label.toLowerCase()}`} tone="text-dark-500" items={[]} note={c.error} />
          ))}
          {!!s?.waiting.length && (
            <div className="mt-3 pt-3 border-t border-surface-border">
              <p className="text-caption font-medium text-dark-500">Waiting for iFAST to confirm (safe to deploy)</p>
              {s.waiting.map((w) => <p key={w.key} className="text-caption text-dark-400">{w.label}: {w.count}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, tone, items, note }: { title: string; tone: string; items: Item[]; note?: string }) {
  return (
    <div className="mt-3">
      <p className={cn("text-caption font-semibold", tone)}>{title}</p>
      {note && <p className="text-2xs text-dark-400">{note}</p>}
      {items.map((it, i) => (
        <p key={i} className="text-caption text-dark-600 flex justify-between gap-3">
          <span className="truncate">{it.label || "—"}</span><span className="text-dark-400 whitespace-nowrap">{ago(it.since)}</span>
        </p>
      ))}
    </div>
  );
}
